// chequePress.ts — A CHEQUE PAYMENT IS ONE PRESS (Screens, 26 Oct 2026: the cheque's seller leg and its delivery).
//
// The routes were made idempotent in 26 Oct's BFF (Mechanisms, 3e560ed8): a seller payment is named by a KEY the screen mints once per press (`leg_key`) and is
// counted once whatever the cheque's stage is by then; a delivery that has started is finished only with the figures it started with (`delivery_started`);
// a payment the ledger holds that the meter never counted blocks the next one until it is sent again as it was (`leg_uncounted`, listed on the deal read as
// `mc_uncounted_legs`). All of that holds only if the SCREEN does its half: the key and the whole body are fixed when the operator presses — the key, the
// amount, the rate, the rail, "this is the last payment", the index — kept until the books ANSWER, and sent again UNCHANGED. A retry that recomputed any
// of it from a read that had moved since (the paid total after a refresh, the balance a delivery suggests once its own balance leg is on the customer's
// box) is a different press: that is how a lost answer became a second payment.
//
//   · A press is HELD from the moment it is made. While it is held the form is not drawn: the screen shows the held press and one button.
//   · An answer ends it: a 2xx (counted — or counted already: `deduped`) or a 4xx (the books looked and refused: nothing is left to resend; the screen
//     re-reads the cheque and the form comes back). Anything else — a 5xx, a dropped connection, no answer — leaves it held, and the one button sends it
//     again as it was. The held press lives in the page (this module), not in a component: leaving the cheque and coming back finds it.
//   · It is WRITTEN DOWN BEFORE IT IS SENT (localStorage, ONE ENTRY PER PRESS — never a shared entry that one tab reads and another replaces) and read back on a reload and by another tab: a payment that was counted
//     while its answer was lost is on neither list the books give (counted is not "uncounted"), so a reload that forgot the press would draw the form again and a
//     second press would mint a second key (Astra, 26 Oct). A restored press is always "unresolved": nobody knows what became of the request that was out.
//     "Send again" then settles it — counted already, or counted now — and the entry goes. An unresolved press has NO age limit (its payment may be counted and on
//     neither list), and it cannot be discarded: a seller payment is only ever sent again, never abandoned from the screen. A cheque that has moved on (its stage is no
//     longer the press's) drops its entry when the screen sees it. A press that cannot be written down is NOT sent (PressNotSaved): without the copy, a lost answer
//     would be a new key after a reload.
//   · What a reload loses the books keep: the payment the ledger holds and the meter never counted is on the deal read (`mc_uncounted_legs`) with what it takes
//     to send it again; a delivery that has started is on it too (`mc_delivery_started`). Both are finished from THOSE figures, never from the form.
//
// Pure but for the React hook at the foot; tests/cheque-press drives all of it.

import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './errors';
import { tick } from './readClock';
import type { McDeliveryStarted, McUncountedLeg } from './api';

// ── the key ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** What the route accepts as a leg key: 16–64 of A–Z a–z 0–9 _ - (crypto.randomUUID() qualifies). */
export const LEG_KEY = /^[A-Za-z0-9_-]{16,64}$/;
export const isLegKey = (k: unknown): k is string => typeof k === 'string' && LEG_KEY.test(k);

/** A new key: a random UUID, or — on a webview without randomUUID — 32 random hex digits. Throws rather than send one the route would refuse. */
export function mintLegKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  let k: string;
  if (c && typeof c.randomUUID === 'function') k = c.randomUUID();
  else if (c && typeof c.getRandomValues === 'function') k = Array.from(c.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
  else throw new Error('This device cannot make a safe payment key.');
  if (!isLegKey(k)) throw new Error('The payment key was not made properly — try again.');
  return k;
}

// ── the seller's payment ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface SellerLegBody {
  desk_treasury_slug: string;
  desk_pay_asset: string;
  desk_pay_amount: number;
  partial: true;
  complete: boolean;
  /** Beside leg_key until the Books Worker retires it: an older Worker names a leg by it, and without it every leg would be leg 1 there. */
  leg_index?: number;
  leg_key?: string;
  rate_to_aed?: number;
}

/** What the form says at the moment of the press. `remaining` and `legAed` are in AED (the owed figure less what is paid; what this leg adds to the meter). */
export interface SellerLegInput {
  rail: string;
  asset: string;
  amount: number;
  isCoin: boolean;
  rateToAed: number;
  remaining: number;
  legAed: number;
  paidSoFar: number;
  /** One AED — the settle engine's dust: a leg within it of the owed figure closes the stage on the same press. */
  dust: number;
}

/** The body of a seller payment, fixed at the press: it is what is held and what is sent again. `complete` is decided HERE, once — a leg that lands within dust of
 *  the owed figure closes the stage; recomputed on a retry from a refreshed total it could close the stage on a payment that did not reach it, or leave it open
 *  on one that did. `leg_index` is the paid total in fils + 1 as the screen read it when the operator pressed (the older routes' name for the leg). */
export function sellerLegBody(i: SellerLegInput, key: string): SellerLegBody {
  return {
    desk_treasury_slug: i.rail,
    desk_pay_asset: i.asset,
    desk_pay_amount: i.amount,
    partial: true,
    complete: Math.abs(i.remaining - Math.round(i.legAed * 100) / 100) <= i.dust,
    leg_index: Math.round(i.paidSoFar * 100) + 1,
    leg_key: key,
    ...(i.isCoin ? { rate_to_aed: i.rateToAed } : {}),
  };
}

/** The body that sends an UNCOUNTED payment again as it was (GET /api/trades/mc/:id → mc_uncounted_legs): the figures are the ledger's, not the form's. null when
 *  the entry does not say enough (posted before 26 Oct: no rail, asset or stage; a coin leg with no rate; neither a key nor an index) — the screen then says so
 *  and offers no button. A keyed entry is sent by its key alone, as the route asks; an older one by its index. */
export function finishLegBody(u: McUncountedLeg): SellerLegBody | null {
  if (!u.desk_treasury_slug || !u.desk_pay_asset || typeof u.complete !== 'boolean') return null;
  if (!(Number(u.desk_pay_amount) > 0)) return null;
  const coin = u.desk_pay_asset !== 'AED';
  if (coin && !(Number(u.rate_to_aed) > 0)) return null;
  const id = isLegKey(u.leg_key) ? { leg_key: u.leg_key } : u.leg_index != null && Number.isFinite(u.leg_index) ? { leg_index: u.leg_index } : null;
  if (!id) return null;
  return {
    desk_treasury_slug: u.desk_treasury_slug,
    desk_pay_asset: u.desk_pay_asset,
    desk_pay_amount: Number(u.desk_pay_amount),
    partial: true,
    complete: u.complete,
    ...id,
    ...(coin ? { rate_to_aed: Number(u.rate_to_aed) } : {}),
  };
}

/** Is this press already COUNTED by the cheque's meter? `counted` is the deal read's `mc_counted_legs` (absent on an older Worker: then nothing is known and the press stays). A press
 *  sent under a key is counted when its key is listed; one with no key (an older payment finished by its index) when its index is listed on a row that has no key. */
export function isCounted(body: { leg_key?: string; leg_index?: number }, counted: ReadonlyArray<{ leg_key: string | null; leg_index: number | null }> | null | undefined): boolean {
  if (!Array.isArray(counted)) return false;
  if (isLegKey(body.leg_key)) return counted.some((c) => c.leg_key === body.leg_key);
  return typeof body.leg_index === 'number' && counted.some((c) => c.leg_key == null && c.leg_index === body.leg_index);
}

// ── the delivery ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface DeliveryBody {
  customer_balance_amount: number;
  customer_fee_bps: number;
  rate_to_aed?: number;
  customer_pay_asset?: string;
  customer_treasury_slug?: string;
  confirm_money_moved_again?: boolean;
}

/** The body of a delivery, fixed at the press. `again` is the operator's answer to the "already paid on account" refusal, never a default. */
export function deliveryBody(i: { balance: number; feeBps: number; coin: boolean; rateToAed: number; again?: boolean }): DeliveryBody {
  return {
    customer_balance_amount: i.balance,
    customer_fee_bps: i.feeBps,
    ...(i.coin ? { rate_to_aed: i.rateToAed } : {}),
    ...(i.again ? { confirm_money_moved_again: true } : {}),
  };
}

/** The body that finishes a STARTED delivery with the figures it started with (mc_delivery_started.press): the balance, its asset and rail, the fee and — for coin —
 *  the rate. null when the books recorded no press (posted before 26 Oct: only the balance is on record) — the screen then says what the balance is and leaves the form. */
export function finishDeliveryBody(s: McDeliveryStarted | null | undefined): DeliveryBody | null {
  const p = s?.press;
  if (!p) return null;
  return {
    customer_balance_amount: p.customer_balance_amount,
    customer_pay_asset: p.customer_pay_asset,
    customer_treasury_slug: p.customer_treasury_slug,
    customer_fee_bps: p.customer_fee_bps,
    ...(p.rate_to_aed != null ? { rate_to_aed: p.rate_to_aed } : {}),
  };
}

// ── what an answer settles ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** Did the books ANSWER this press — look at it and refuse (a 4xx: nothing is held for it, nothing left to resend) — or is its outcome unknown (a 5xx, a dropped
 *  connection, no answer, anything else): it may have been counted. The route answers a 4xx before it writes, or after a write the deal read lists as uncounted,
 *  which is finished from that list; an unknown outcome is resent as it was and counted once. */
export function answerOf(e: unknown): 'refused' | 'unknown' {
  return e instanceof ApiError && e.status >= 400 && e.status < 500 ? 'refused' : 'unknown';
}

// ── the held press ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

export type PressScope = 'seller-leg' | 'delivery';
export interface Held<B> {
  readonly scope: PressScope;
  readonly dealId: string;
  /** This press's own name on the disk. ONE ENTRY PER PRESS: no tab ever reads an entry and deletes it a moment later, so no tab can erase a press another one began (a read followed by a
   *  delete is not atomic across tabs — Astra, round 3). */
  readonly id: string;
  /** Frozen: a copy of what was pressed; nothing the form does afterwards reaches it. */
  readonly body: Readonly<B>;
  /** sending — a request is out; unresolved — the last one got no answer and none is out: the one button sends it again. */
  readonly state: 'sending' | 'unresolved';
  readonly out: number;
}

const held = new Map<string, Held<unknown>>();
/** The tick (lib/readClock) at which a press of this cheque and stage last ENDED, in this page (scope:cheque → tick). The stage's form waits for a deal read that STARTED after it — not one that
 *  merely finished after it: a read sent before the payment was counted can come back later with the old paid total (Astra, round 5). The marker lives HERE, not in a component, so leaving the
 *  cheque and coming back (a remount with the same cached read) cannot forget it (round 4). Cleared once a read that started after it has been seen. */
const endedTick = new Map<string, number>();
const listeners = new Set<() => void>();
const keyOf = (scope: PressScope, dealId: string) => `${scope}:${dealId}`;
const changed = () => { for (const l of [...listeners]) l(); };
const frozen = <B,>(body: B): Readonly<B> => Object.freeze(JSON.parse(JSON.stringify(body)) as B);

// ── written down before it is sent — one entry per press ───────────────────────────────────────────────────────────────────────────────────────
const STORED = 'dubaiotc.press.';
/** `dubaiotc.press.<scope>:<dealId>#<id>`: the cheque and stage the press belongs to, and its own id. */
const ENTRY = /^(seller-leg|delivery):(.+)#([A-Za-z0-9_-]{8,64})$/;
const disk = (): Storage | null => { try { return globalThis.localStorage ?? null; } catch { return null; } };
const entryKey = (scope: PressScope, dealId: string, id: string) => `${STORED}${scope}:${dealId}#${id}`;
interface Entry { scope: PressScope; dealId: string; id: string; body: object; at: number }

/** begin() could not keep a copy of the press on this device, so it did not start: nothing was sent. */
export class PressNotSaved extends Error {
  constructor() { super('This device cannot keep a record of the payment, so it was not sent.'); this.name = 'PressNotSaved'; }
}
/** The one line a screen says when that happens (the reason is behind its Details). */
export const NOT_SAVED_WORDS = 'Not sent — can\'t keep a copy on this device.';
export const NOT_SAVED_MORE = 'A payment is sent only after a copy of it is kept on this device, so an answer that is lost can be finished after a reload. Allow site data for this page, then try again.';

/** Write the press down and read it back: true only when the disk really holds it. */
function writeDown(scope: PressScope, dealId: string, id: string, body: unknown): boolean {
  try {
    const d = disk();
    if (!d) return false;
    d.setItem(entryKey(scope, dealId, id), JSON.stringify({ body, at: Date.now() }));
    return d.getItem(entryKey(scope, dealId, id)) !== null;
  } catch { return false; }
}
/** Remove ONE press's entry, by its own key — nothing is read first. */
function removeEntry(scope: PressScope, dealId: string, id: string): void { try { disk()?.removeItem(entryKey(scope, dealId, id)); } catch { /* nothing to remove */ } }
/** What the disk holds, parsed — a damaged entry (or one not named like a press) is removed on the way. No entry expires by age: a press that has no answer yet may be a counted payment. */
function readDown(): Entry[] {
  const out: Entry[] = [];
  const d = disk();
  if (!d) return out;
  try {
    const keys: string[] = [];
    for (let i = 0; i < d.length; i++) { const k = d.key(i); if (k && k.startsWith(STORED)) keys.push(k); }
    for (const full of keys) {
      try {
        const m = ENTRY.exec(full.slice(STORED.length));
        const v = JSON.parse(d.getItem(full) ?? 'null') as { body?: unknown; at?: number } | null;
        if (!m || !v || typeof v.body !== 'object' || v.body === null || Array.isArray(v.body)) { d.removeItem(full); continue; }
        out.push({ scope: m[1] as PressScope, dealId: m[2]!, id: m[3]!, body: v.body as object, at: typeof v.at === 'number' ? v.at : 0 });
      } catch { try { d.removeItem(full); } catch { /* leave it */ } }
    }
  } catch { /* storage unreadable: nothing is restored */ }
  return out;
}
/** Bring the page's presses in line with the disk; true when something here changed. A press this page holds with no request out whose entry is gone from the disk has ended, wherever it
 *  ended. A cheque and stage this page holds nothing for, with entries on the disk, takes the OLDEST of them as a press waiting to be sent again (a newer one waits its turn: it appears
 *  when this one ends). */
function syncWithDisk(): boolean {
  const entries = readDown();
  let moved = false;
  for (const [k, h] of [...held]) {
    if (h.out === 0 && !entries.some((e) => e.scope === h.scope && e.dealId === h.dealId && e.id === h.id)) { held.delete(k); endedTick.set(k, tick()); moved = true; }
  }
  const oldest = new Map<string, Entry>();
  for (const e of entries) {
    const k = keyOf(e.scope, e.dealId), cur = oldest.get(k);
    if (!cur || e.at < cur.at || (e.at === cur.at && e.id < cur.id)) oldest.set(k, e);
  }
  for (const [k, e] of oldest) {
    if (held.has(k)) continue;
    held.set(k, { scope: e.scope, dealId: e.dealId, id: e.id, body: frozen(e.body), state: 'unresolved', out: 0 });
    moved = true;
  }
  return moved;
}
syncWithDisk();
if (typeof (globalThis as { addEventListener?: unknown }).addEventListener === 'function') {
  (globalThis as unknown as Window).addEventListener('storage', (e: StorageEvent) => {
    if (e.key === null || e.key.startsWith(STORED)) { syncWithDisk(); changed(); }
  });
}

export const presses = {
  get<B>(scope: PressScope, dealId: string): Held<B> | null {
    return (held.get(keyOf(scope, dealId)) as Held<B> | undefined) ?? null;
  },
  /** Begin a press: held, written down under its own entry, with one request out. null when one is already held for this cheque and stage — here, or waiting on the disk for another tab's
   *  — so a second tap starts nothing. THROWS PressNotSaved when it cannot be written down: it is then not held and must not be sent. */
  begin<B>(scope: PressScope, dealId: string, body: B): Held<B> | null {
    if (syncWithDisk()) changed();
    const k = keyOf(scope, dealId);
    if (held.has(k)) return null;
    const h: Held<B> = { scope, dealId, id: mintLegKey(), body: frozen(body), state: 'sending', out: 1 };
    if (!writeDown(scope, dealId, h.id, h.body)) throw new PressNotSaved();
    held.set(k, h);
    changed();
    return h;
  },
  /** Send the held press again, as it was. null unless it is waiting for that (a second tap while one is out sends nothing). */
  resend<B>(scope: PressScope, dealId: string): Held<B> | null {
    const k = keyOf(scope, dealId);
    const h = held.get(k);
    if (!h || h.state !== 'unresolved') return null;
    const next: Held<unknown> = { ...h, state: 'sending', out: h.out + 1 };
    held.set(k, next);
    changed();
    return next as Held<B>;
  },
  /** A request answered. 'done' (counted) and 'refused' (the books looked and said no) end the press; 'unknown' keeps it — waiting to be sent again once
   *  no request of its own is still out. `sent` is the body the request carried: an answer to a press that has since ended (and been replaced by another)
   *  touches nothing. */
  answered(scope: PressScope, dealId: string, how: 'done' | 'refused' | 'unknown', sent: object): void {
    const k = keyOf(scope, dealId);
    const h = held.get(k);
    if (!h || h.body !== sent) return;
    if (how === 'unknown') {
      const out = Math.max(0, h.out - 1);
      held.set(k, { ...h, out, state: out > 0 ? 'sending' : 'unresolved' });
    } else {
      held.delete(k);
      endedTick.set(k, tick());           // the stage now waits for a deal read that STARTS after this
      removeEntry(scope, dealId, h.id);   // its OWN entry, by its own key: a newer press another tab began is a different entry and is never touched
      syncWithDisk();                     // …and one that was waiting behind it appears here
    }
    changed();
  },
  /** End a press without an answer. With an `id`, that press (a held delivery discarded; the entry of a waiting press); without one, every press of this cheque and stage (the cheque has moved on
   *  — its stage is no longer this press's). Here and on the disk. */
  drop(scope: PressScope, dealId: string, id?: string): void {
    for (const e of readDown()) if (e.scope === scope && e.dealId === dealId && (id === undefined || e.id === id)) removeEntry(scope, dealId, e.id);
    const k = keyOf(scope, dealId), h = held.get(k);
    if (h && (id === undefined || h.id === id)) { held.delete(k); if (id !== undefined) endedTick.set(k, tick()); }
    if (id === undefined) endedTick.delete(k);   // the cheque has moved on: nothing here is left to wait for
    syncWithDisk();
    changed();
  },
  /** The tick at which a press of this cheque and stage last ended in this page, while no deal read that started after it has been seen (0: none). */
  endedAt(scope: PressScope, dealId: string): number { return endedTick.get(keyOf(scope, dealId)) ?? 0; },
  /** A deal read that STARTED at `readTick` has been seen: if that is after the last end, the stage no longer has to wait. A read that started before it — or at no tick at all — changes nothing. */
  readSeen(scope: PressScope, dealId: string, readTick: number | undefined): void {
    const k = keyOf(scope, dealId), t = endedTick.get(k);
    if (t !== undefined && !awaitsFreshRead(t, readTick)) { endedTick.delete(k); changed(); }
  },
  /** Read the disk again (another tab wrote or ended a press). Tests drive this; a `storage` event does the same. */
  reload(): void { syncWithDisk(); changed(); },
  subscribe(l: () => void): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
  /** For tests: forget everything, in memory and on the disk. */
  clear(): void {
    held.clear();
    endedTick.clear();
    try { const d = disk(); if (d) for (const e of readDown()) d.removeItem(entryKey(e.scope, e.dealId, e.id)); } catch { /* nothing to clear */ }
    changed();
  },
};

/** begin() for a screen: null when a press is already held, or when it could not be written down — `say` is then given the one line to show (and null otherwise, to clear it). Nothing is sent
 *  in either case. */
export function tryBegin<B>(scope: PressScope, dealId: string, body: B, say: (words: string | null) => void): Held<B> | null {
  say(null);
  try { return presses.begin(scope, dealId, body); }
  catch (e) { if (e instanceof PressNotSaved) { say(NOT_SAVED_WORDS); return null; } throw e; }
}

/** The held press of this cheque and stage, live: a component that unmounts and comes back finds it. */
export function useHeld<B>(scope: PressScope, dealId: string): Held<B> | null {
  const snap = () => presses.get<B>(scope, dealId);
  return useSyncExternalStore(presses.subscribe, snap, snap);
}

/** The tick at which a press of this cheque and stage last ended, while no deal read that started after it has been seen (0: none), live — the stage's form waits while the deal read it
 *  holds started before it. */
export function useEnded(scope: PressScope, dealId: string): number {
  const snap = () => presses.endedAt(scope, dealId);
  return useSyncExternalStore(presses.subscribe, snap, snap);
}

/** Must the stage still wait for a fresh read? A press ended at tick `endedAt` (0: none), and the deal read in hand started at `readTick` (none: not a stamped read). Only a read that STARTED
 *  after the end can say what the press did; one that finished after it but started before it cannot — it may have been answered before the payment was counted. */
export function awaitsFreshRead(endedAt: number, readTick: number | undefined): boolean {
  return endedAt > 0 && !((readTick ?? 0) > endedAt);
}

/** The cheque's read, in the query cache: ONE key, for the page that shows it and for the stage that asks for it again. */
export const dealKey = (dealId: string) => ['mc', 'deal', dealId] as const;

/** Ask the books for the cheque again — THE EXACT CHEQUE, whether or not anyone is subscribed to it at this moment, replacing a read that is already out. `invalidateQueries` is not that: it
 *  refetches only ACTIVE queries, and a stage that has just come back to the screen runs its effects BEFORE the page above it subscribes to the query (React runs a child's effects first), so
 *  its invalidation found nobody, the page then joined the old read that was still out, and nothing asked again (Astra, round 6). `refetchQueries({ type: 'all' })` reaches the query either way
 *  and cancels the read in flight, so the read that answers is one that started after this call. */
export function askForCheque(qc: Pick<QueryClient, 'refetchQueries'>, dealId: string): Promise<void> {
  return qc.refetchQueries({ queryKey: dealKey(dealId), type: 'all' });
}

/** What the stage can see of the cheque's read. */
export interface ReadState {
  /** The tick the deal read in hand started at (0: none, or not one this page started). */
  readonly tick: number;
  /** A read of the cheque is out. */
  readonly reading: boolean;
  /** The last read failed: the stage says so and offers "Try again" — it does not ask by itself. */
  readonly failed: boolean;
}

/** AFTER A HELD PRESS ENDS — counted here, counted or refused in another tab, settled by the books' own list, discarded — the stage's form is not drawn until the cheque has been READ AGAIN by a read that
 *  STARTED after the end (see `awaitsFreshRead`); the tick the press ended at lives in the press store, per cheque and stage, not in the component, so a remount with the same cached read still waits
 *  (Astra, rounds 3–5). True while the stage must wait. Three effects:
 *   1. once per end (`onEnd`): whatever the stage must forget — the payment typed for the press that ended;
 *   2. the stage must always have a read COMING: when it sees an end it asks for the cheque again — once per end per mount, whatever is out, because a read that is out may be from before the end — and
 *      afterwards, whenever it still waits with NO read out and no failure to show (a read that landed from before the end; one the page above joined instead of starting): ask again. A read that failed
 *      is not asked again by itself — the stage says so and the operator presses "Try again". There is no state in which the stage waits and nothing is on its way (round 6);
 *   3. the marker is cleared once a read that started after it has been seen. */
export function useReconcile(scope: PressScope, dealId: string, read: ReadState, ask: () => void, onEnd?: () => void): boolean {
  const endedAt = useEnded(scope, dealId);
  const waits = awaitsFreshRead(endedAt, read.tick);
  const latest = useRef({ ask, onEnd });
  latest.current = { ask, onEnd };
  const askedFor = useRef(0);
  useEffect(() => { if (waits) latest.current.onEnd?.(); }, [endedAt]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!waits || read.failed) return;
    if (askedFor.current !== endedAt) { askedFor.current = endedAt; latest.current.ask(); return; }
    if (!read.reading) latest.current.ask();
  }, [waits, read.reading, read.failed, read.tick, endedAt]);
  useEffect(() => { if (endedAt > 0 && !waits) presses.readSeen(scope, dealId, read.tick); }, [endedAt, read.tick, scope, dealId]);   // eslint-disable-line react-hooks/exhaustive-deps
  return waits;
}

/** Does the books' own list of counted payments settle this kept press? Only one that WAITS (no request out): its own answer settles one that is out. */
export function countedSettles(h: Held<{ leg_key?: string; leg_index?: number }> | null, counted: ReadonlyArray<{ leg_key: string | null; leg_index: number | null }> | null | undefined): boolean {
  return !!h && h.state === 'unresolved' && isCounted(h.body, counted);
}
